import {
  User,
  UserRole,
  Permission,
} from '../types/index';

// Mock Permissions
export const mockPermissions: Permission[] = [
  // User Management Permissions
  { id: 'users-read', name: 'View Users', resource: 'users', action: 'read', description: 'View user accounts and profiles', category: 'users' },
  { id: 'users-write', name: 'Manage Users', resource: 'users', action: 'write', description: 'Create and edit user accounts', category: 'users' },
  { id: 'users-delete', name: 'Delete Users', resource: 'users', action: 'delete', description: 'Delete user accounts', category: 'users' },
  { id: 'users-admin', name: 'User Administration', resource: 'users', action: 'admin', description: 'Full user management including roles', category: 'users' },

  // Client Management Permissions
  { id: 'clients-read', name: 'View Clients', resource: 'clients', action: 'read', description: 'View client profiles and information', category: 'clients' },
  { id: 'clients-write', name: 'Manage Clients', resource: 'clients', action: 'write', description: 'Create and edit client profiles', category: 'clients' },
  { id: 'clients-delete', name: 'Delete Clients', resource: 'clients', action: 'delete', description: 'Delete client accounts', category: 'clients' },
  { id: 'clients-admin', name: 'Client Administration', resource: 'clients', action: 'admin', description: 'Full client management access', category: 'clients' },

  // Coach Management Permissions
  { id: 'coaches-read', name: 'View Coaches', resource: 'coaches', action: 'read', description: 'View coach profiles and schedules', category: 'coaches' },
  { id: 'coaches-write', name: 'Manage Coaches', resource: 'coaches', action: 'write', description: 'Create and edit coach profiles', category: 'coaches' },
  { id: 'coaches-delete', name: 'Delete Coaches', resource: 'coaches', action: 'delete', description: 'Delete coach accounts', category: 'coaches' },
  { id: 'coaches-admin', name: 'Coach Administration', resource: 'coaches', action: 'admin', description: 'Full coach management access', category: 'coaches' },

  // Program Management Permissions
  { id: 'programs-read', name: 'View Programs', resource: 'programs', action: 'read', description: 'View training programs and templates', category: 'programs' },
  { id: 'programs-write', name: 'Manage Programs', resource: 'programs', action: 'write', description: 'Create and edit training programs', category: 'programs' },
  { id: 'programs-delete', name: 'Delete Programs', resource: 'programs', action: 'delete', description: 'Delete training programs', category: 'programs' },
  { id: 'programs-admin', name: 'Program Administration', resource: 'programs', action: 'admin', description: 'Full program management access', category: 'programs' },

  // Reports & Analytics Permissions
  { id: 'reports-read', name: 'View Reports', resource: 'reports', action: 'read', description: 'View business reports and analytics', category: 'reports' },
  { id: 'reports-write', name: 'Create Reports', resource: 'reports', action: 'write', description: 'Create custom reports', category: 'reports' },
  { id: 'reports-delete', name: 'Delete Reports', resource: 'reports', action: 'delete', description: 'Delete custom reports', category: 'reports' },
  { id: 'reports-admin', name: 'Report Administration', resource: 'reports', action: 'admin', description: 'Full report management access', category: 'reports' },

  // System Settings Permissions
  { id: 'settings-read', name: 'View Settings', resource: 'settings', action: 'read', description: 'View system configuration', category: 'settings' },
  { id: 'settings-write', name: 'Manage Settings', resource: 'settings', action: 'write', description: 'Modify system configuration', category: 'settings' },
  { id: 'settings-admin', name: 'Settings Administration', resource: 'settings', action: 'admin', description: 'Full system administration access', category: 'settings' },

  // Billing Permissions
  { id: 'billing-read', name: 'View Billing', resource: 'billing', action: 'read', description: 'View billing and payment information', category: 'billing' },
  { id: 'billing-write', name: 'Manage Billing', resource: 'billing', action: 'write', description: 'Process payments and manage billing', category: 'billing' },
  { id: 'billing-admin', name: 'Billing Administration', resource: 'billing', action: 'admin', description: 'Full billing management access', category: 'billing' },
];

// Mock User Roles
export const mockUserRoles: UserRole[] = [
  {
    id: 'super-admin',
    name: 'Super Admin',
    description: 'Full system access with all permissions',
    level: 100,
    permissions: mockPermissions,
    isCustom: false,
    createdAt: '2024-01-01T00:00:00Z',
    userCount: 1,
  },
  {
    id: 'admin',
    name: 'Admin',
    description: 'Administrative access with most permissions',
    level: 80,
    permissions: mockPermissions.filter(p => p.action !== 'admin' || p.category !== 'settings'),
    isCustom: false,
    createdAt: '2024-01-01T00:00:00Z',
    userCount: 3,
  },
  {
    id: 'manager',
    name: 'Manager',
    description: 'Management access to clients and coaches',
    level: 60,
    permissions: mockPermissions.filter(p => 
      ['clients', 'coaches', 'programs', 'reports'].includes(p.category) && p.action !== 'delete'
    ),
    isCustom: false,
    createdAt: '2024-01-01T00:00:00Z',
    userCount: 5,
  },
  {
    id: 'coach',
    name: 'Coach',
    description: 'Access to assigned clients and programs',
    level: 40,
    permissions: mockPermissions.filter(p => 
      ['clients', 'programs'].includes(p.category) && ['read', 'write'].includes(p.action)
    ),
    isCustom: false,
    createdAt: '2024-01-01T00:00:00Z',
    userCount: 12,
  },
  {
    id: 'viewer',
    name: 'Viewer',
    description: 'Read-only access to basic information',
    level: 20,
    permissions: mockPermissions.filter(p => p.action === 'read'),
    isCustom: false,
    createdAt: '2024-01-01T00:00:00Z',
    userCount: 8,
  },
  {
    id: 'custom-trainer',
    name: 'Senior Trainer',
    description: 'Custom role for senior trainers with extended permissions',
    level: 50,
    permissions: mockPermissions.filter(p => 
      ['clients', 'programs', 'reports'].includes(p.category) && p.action !== 'admin'
    ),
    isCustom: true,
    createdAt: '2024-03-15T10:30:00Z',
    userCount: 4,
  },
];

// Mock Users
export const mockUsers: User[] = [
  {
    id: 'user-1',
    username: 'admin',
    email: 'admin@fitmanagement.com',
    firstName: 'John',
    lastName: 'Smith',
    role: mockUserRoles[0], // Super Admin
    status: 'active',
    lastLogin: '2024-06-15T09:30:00Z',
    createdAt: '2024-01-01T00:00:00Z',
    permissions: mockUserRoles[0].permissions,
    isActive: true,
    department: 'Administration',
    phoneNumber: '+1-555-0101',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=John',
  },
  {
    id: 'user-2',
    username: 'sarah.johnson',
    email: 'sarah.johnson@fitmanagement.com',
    firstName: 'Sarah',
    lastName: 'Johnson',
    role: mockUserRoles[2], // Manager
    status: 'active',
    lastLogin: '2024-06-15T08:45:00Z',
    createdAt: '2024-01-15T10:00:00Z',
    permissions: mockUserRoles[2].permissions,
    isActive: true,
    department: 'Operations',
    phoneNumber: '+1-555-0102',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sarah',
  },
  {
    id: 'user-3',
    username: 'mike.chen',
    email: 'mike.chen@fitmanagement.com',
    firstName: 'Mike',
    lastName: 'Chen',
    role: mockUserRoles[3], // Coach
    status: 'active',
    lastLogin: '2024-06-15T07:20:00Z',
    createdAt: '2024-02-01T09:30:00Z',
    permissions: mockUserRoles[3].permissions,
    isActive: true,
    department: 'Coaching',
    phoneNumber: '+1-555-0103',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Mike',
  },
  {
    id: 'user-4',
    username: 'emily.davis',
    email: 'emily.davis@fitmanagement.com',
    firstName: 'Emily',
    lastName: 'Davis',
    role: mockUserRoles[5], // Senior Trainer (Custom)
    status: 'active',
    lastLogin: '2024-06-14T16:30:00Z',
    createdAt: '2024-02-15T11:15:00Z',
    permissions: mockUserRoles[5].permissions,
    isActive: true,
    department: 'Training',
    phoneNumber: '+1-555-0104',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Emily',
  },
  {
    id: 'user-5',
    username: 'alex.rodriguez',
    email: 'alex.rodriguez@fitmanagement.com',
    firstName: 'Alex',
    lastName: 'Rodriguez',
    role: mockUserRoles[4], // Viewer
    status: 'inactive',
    lastLogin: '2024-06-10T14:20:00Z',
    createdAt: '2024-03-01T13:45:00Z',
    permissions: mockUserRoles[4].permissions,
    isActive: false,
    department: 'Reception',
    phoneNumber: '+1-555-0105',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Alex',
  },
  {
    id: 'user-6',
    username: 'jessica.wilson',
    email: 'jessica.wilson@fitmanagement.com',
    firstName: 'Jessica',
    lastName: 'Wilson',
    role: mockUserRoles[1], // Admin
    status: 'suspended',
    lastLogin: '2024-06-01T12:00:00Z',
    createdAt: '2024-03-10T15:20:00Z',
    permissions: mockUserRoles[1].permissions,
    isActive: false,
    department: 'IT',
    phoneNumber: '+1-555-0106',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Jessica',
  },
]; 